import React, { useEffect, useMemo, useState } from 'react';
import {
  IconChevronDown,
  IconChevronUp,
  IconDevices,
  IconHeart,
  IconHeartFilled,
  IconMinimize,
  IconMusic,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerSkipBack,
  IconPlayerSkipForward,
  IconRepeat,
  IconArrowsShuffle,
  IconVolume,
  IconVolumeOff,
} from '@tabler/icons-react';
import toast from 'react-hot-toast';

import { useAppleMusicPlayer } from '@components/Athlete/AppleMusicPlayer/useAppleMusicPlayer';
import {
  fetchAppleMusicPlaylists,
  getActiveWorkoutPlaylist,
  setActiveWorkoutPlaylist,
} from '@util/appleMusicSession';
import { toErrorMessage } from '@util/toErrorMessage';
import {
  artistNames,
  formatClock,
  formatRemaining,
} from '@components/Athlete/AppleMusicPlayer/playerFormat';
import classes from './SpotifyPlayer.module.css';

function Cover({ src, className, size = 28 }) {
  if (!src) {
    return (
      <div className={className}>
        <IconMusic size={size} stroke={1.75} />
      </div>
    );
  }
  return <img src={src} alt="" className={className} />;
}

function idleTrackLabels(connected, ready) {
  if (!connected) {
    return {
      title: 'Apple Music not connected',
      subtitle: 'Open Music to connect',
    };
  }
  if (!ready) {
    return {
      title: 'Nothing playing',
      subtitle: 'Authorize Apple Music to play…',
    };
  }
  return {
    title: 'Nothing playing',
    subtitle: 'Pick a playlist to start',
  };
}

function normalizeQuickPlaylist(playlist) {
  if (!playlist) return null;
  const id = playlist.id || playlist.playlistId;
  if (!id) return null;
  return {
    id,
    name: playlist.name || playlist.catalog_name || 'Playlist',
    image_url: playlist.image_url || playlist.imageUrl || null,
    uri: playlist.uri || `applemusic:playlist:${id}`,
    tagline: playlist.tagline || null,
  };
}

function buildQuickPlaylists(featured, active) {
  const cards = [];
  const seen = new Set();

  function push(raw) {
    const playlist = normalizeQuickPlaylist(raw);
    if (!playlist || seen.has(playlist.id)) return;
    seen.add(playlist.id);
    cards.push(playlist);
  }

  push(active);
  (featured || []).forEach(push);
  return cards.slice(0, 3);
}

function NowPlayingBody({ compact = false }) {
  const {
    setNowPlayingOpen,
    playback,
    busy,
    ready,
    connected,
    playPlaylist,
    togglePlay,
    next,
    previous,
    seek,
    setShuffle,
    setRepeat,
    toggleSave,
  } = useAppleMusicPlayer();

  const [featured, setFeatured] = useState([]);
  const [activePlaylist, setActivePlaylist] = useState(() =>
    getActiveWorkoutPlaylist(),
  );

  useEffect(() => {
    if (!connected) {
      setFeatured([]);
      return undefined;
    }
    let cancelled = false;
    // One light library call — not the full Music home catalog resolve.
    fetchAppleMusicPlaylists({ limit: 3, offset: 0 })
      .then((payload) => {
        if (cancelled) return;
        setFeatured(payload?.data || []);
      })
      .catch(() => {
        if (!cancelled) setFeatured([]);
      });
    return () => {
      cancelled = true;
    };
  }, [connected]);

  useEffect(() => {
    function refreshActive() {
      setActivePlaylist(getActiveWorkoutPlaylist());
    }
    refreshActive();
    window.addEventListener('focus', refreshActive);
    window.addEventListener('storage', refreshActive);
    return () => {
      window.removeEventListener('focus', refreshActive);
      window.removeEventListener('storage', refreshActive);
    };
  }, []);

  const track = playback?.track;
  const progress = Number(playback?.progress_ms || 0);
  const duration = Number(track?.duration_ms || 0);
  const idle = idleTrackLabels(connected, ready);
  const quickPlaylists = useMemo(
    () => buildQuickPlaylists(featured, activePlaylist),
    [featured, activePlaylist],
  );
  const playingContextUri = playback?.context_uri || null;

  function startQuickPlaylist(playlist) {
    const saved = setActiveWorkoutPlaylist({
      playlistId: playlist.id,
      name: playlist.name,
      imageUrl: playlist.image_url,
      uri: playlist.uri,
    });
    if (saved) setActivePlaylist(saved);
    playPlaylist(playlist).catch((error) =>
      toast.error(toErrorMessage(error)),
    );
  }

  return (
    <>
      <div className={classes.sheetHeader}>
        <button
          type="button"
          className={classes.iconBtn}
          onClick={() => setNowPlayingOpen(false)}
          aria-label="Close player"
        >
          <IconChevronDown size={22} stroke={2} />
        </button>
        <div>
          <p className={classes.sheetEyebrow}>Training Mode</p>
          <p className={classes.sheetSub}>Music for a Stronger You.</p>
        </div>
        <span className={classes.iconBtn} aria-hidden>
          <IconMusic size={18} stroke={1.75} />
        </span>
      </div>

      <div
        className={
          compact ? classes.sheetArtWrapCompact : classes.sheetArtWrap
        }
      >
        <Cover
          src={track?.image_url}
          className={track?.image_url ? classes.sheetArt : classes.sheetArtFallback}
          size={compact ? 36 : 48}
        />
      </div>

      <div className={classes.sheetTrackRow}>
        <div>
          <h2 className={classes.sheetTitle}>{track?.name || idle.title}</h2>
          <p className={classes.sheetArtist}>
            {track ? artistNames(track) : idle.subtitle}
          </p>
        </div>
        <button
          type="button"
          className={`${classes.iconBtn} ${track?.is_saved ? classes.iconBtnActive : ''}`}
          onClick={() => toggleSave().catch(() => null)}
          disabled={!track?.id || busy}
          aria-label={track?.is_saved ? 'Remove from Liked Songs' : 'Save to Liked Songs'}
        >
          {track?.is_saved ? (
            <IconHeartFilled size={22} />
          ) : (
            <IconHeart size={22} stroke={2} />
          )}
        </button>
      </div>

      <div className={classes.sheetControls}>
        <div className={classes.scrubberWrap}>
          <p className={classes.time}>{formatClock(progress)}</p>
          <input
            className={classes.scrubber}
            type="range"
            min={0}
            max={duration || 1}
            value={Math.min(progress, duration || 1)}
            onChange={(event) => seek(Number(event.target.value)).catch(() => null)}
            disabled={!track || busy || !ready}
            aria-label="Seek"
          />
          <p className={classes.time}>{formatRemaining(progress, duration)}</p>
        </div>

        <div className={classes.controlsRow}>
          <button
            type="button"
            className={`${classes.iconBtn} ${playback.shuffle_state ? classes.iconBtnActive : ''}`}
            onClick={() => setShuffle(!playback.shuffle_state).catch(() => null)}
            disabled={busy || !ready}
            aria-label="Shuffle"
          >
            <IconArrowsShuffle size={20} stroke={2} />
          </button>
          <button
            type="button"
            className={classes.iconBtn}
            onClick={() => previous().catch(() => null)}
            disabled={busy || !ready}
            aria-label="Previous"
          >
            <IconPlayerSkipBack size={24} stroke={2} />
          </button>
          <button
            type="button"
            className={classes.playBtn}
            onClick={() => togglePlay().catch(() => null)}
            disabled={busy || !ready || !track}
            aria-label={playback.is_playing ? 'Pause' : 'Play'}
          >
            {playback.is_playing ? (
              <IconPlayerPause size={24} stroke={2} />
            ) : (
              <IconPlayerPlay size={24} stroke={2} />
            )}
          </button>
          <button
            type="button"
            className={classes.iconBtn}
            onClick={() => next().catch(() => null)}
            disabled={busy || !ready}
            aria-label="Next"
          >
            <IconPlayerSkipForward size={24} stroke={2} />
          </button>
          <button
            type="button"
            className={`${classes.iconBtn} ${
              playback.repeat_state && playback.repeat_state !== 'off'
                ? classes.iconBtnActive
                : ''
            }`}
            onClick={() => {
              const nextState =
                playback.repeat_state === 'off'
                  ? 'context'
                  : playback.repeat_state === 'context'
                    ? 'track'
                    : 'off';
              setRepeat(nextState).catch(() => null);
            }}
            disabled={busy || !ready}
            aria-label="Repeat"
          >
            <IconRepeat size={20} stroke={2} />
          </button>
        </div>
      </div>

      <div className={classes.playlistRow}>
        {quickPlaylists.length === 0 ? (
          <p className={classes.playlistEmpty}>
            Pick playlists on Music to pin quick starts here.
          </p>
        ) : (
          quickPlaylists.map((playlist) => {
            const isCurrent =
              playingContextUri === playlist.uri ||
              activePlaylist?.playlistId === playlist.id;
            return (
              <button
                key={playlist.id}
                type="button"
                className={`${classes.playlistCard} ${
                  isCurrent ? classes.playlistCardActive : ''
                }`}
                onClick={() => startQuickPlaylist(playlist)}
                disabled={busy || !ready}
              >
                <Cover
                  src={playlist.image_url}
                  className={
                    playlist.image_url
                      ? classes.playlistCardArt
                      : classes.playlistCardArtFallback
                  }
                  size={16}
                />
                <p className={classes.playlistCardLabel}>{playlist.name}</p>
                <p className={classes.playlistCardMeta}>
                  {isCurrent && playback.is_playing
                    ? 'Playing'
                    : playlist.tagline || 'Start'}
                </p>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

export function SpotifyMiniPlayer({
  mode = 'collapsed',
  onExpand,
  onCollapse,
  onHide,
  showProgressLine = true,
  className = '',
}) {
  const {
    playback,
    busy,
    ready,
    connected,
    togglePlay,
    next,
    previous,
    seek,
    setShuffle,
    setRepeat,
    toggleSave,
    transferTo,
    deviceId,
    devices,
    nowPlayingOpen,
    setNowPlayingOpen,
    registerHeaderPlayer,
    volume,
    setVolume,
  } = useAppleMusicPlayer();

  const track = playback?.track;
  const progress = Number(playback?.progress_ms || 0);
  const duration = Number(track?.duration_ms || 0);
  const ratio = duration > 0 ? Math.min(1, progress / duration) : 0;
  const idle = idleTrackLabels(connected, ready);
  const volumeValue = Math.round(Number(volume || 0) * 100);
  const hasDevices = Array.isArray(devices) && devices.length > 0;

  useEffect(() => {
    if (mode !== 'header') return undefined;
    return registerHeaderPlayer();
  }, [mode, registerHeaderPlayer]);

  if (mode === 'header') {
    return (
      <>
        {nowPlayingOpen ? (
          <button
            type="button"
            className={classes.headerExpandBackdrop}
            aria-label="Close expanded player"
            onClick={() => setNowPlayingOpen(false)}
          />
        ) : null}
        <div
          className={`${classes.spotifyGreen} ${classes.headerPlayer} ${
            nowPlayingOpen ? classes.headerPlayerOpen : ''
          } ${className}`.trim()}
        >
          <div className={classes.headerInner}>
            <button
              type="button"
              className={classes.headerTrackBtn}
              onClick={() => setNowPlayingOpen(!nowPlayingOpen)}
              aria-label={nowPlayingOpen ? 'Collapse player' : 'Expand player'}
              aria-expanded={nowPlayingOpen}
            >
              <Cover
                src={track?.image_url}
                className={
                  track?.image_url ? classes.headerArt : classes.headerArtFallback
                }
                size={18}
              />
              <div className={classes.headerMeta}>
                <p className={classes.headerTitle}>
                  {track?.name || idle.title}
                </p>
                <p className={classes.headerArtist}>
                  {track ? artistNames(track) : idle.subtitle}
                </p>
              </div>
            </button>
            <div className={classes.headerControls}>
              <button
                type="button"
                className={classes.headerIconBtn}
                onClick={() => previous().catch(() => null)}
                disabled={busy || !ready}
                aria-label="Previous"
              >
                <IconPlayerSkipBack size={18} stroke={2} />
              </button>
              <button
                type="button"
                className={classes.headerPlayBtn}
                onClick={() => togglePlay().catch(() => null)}
                disabled={busy || !ready || !track}
                aria-label={playback?.is_playing ? 'Pause' : 'Play'}
              >
                {playback?.is_playing ? (
                  <IconPlayerPause size={18} stroke={2} />
                ) : (
                  <IconPlayerPlay size={18} stroke={2} />
                )}
              </button>
              <button
                type="button"
                className={classes.headerIconBtn}
                onClick={() => next().catch(() => null)}
                disabled={busy || !ready}
                aria-label="Next"
              >
                <IconPlayerSkipForward size={18} stroke={2} />
              </button>
              <div className={classes.headerVolume}>
                <button
                  type="button"
                  className={classes.headerIconBtn}
                  onClick={() =>
                    setVolume(volumeValue === 0 ? 0.85 : 0).catch(() => null)
                  }
                  disabled={!ready}
                  aria-label={volumeValue === 0 ? 'Unmute' : 'Mute'}
                >
                  {volumeValue === 0 ? (
                    <IconVolumeOff size={16} stroke={2} />
                  ) : (
                    <IconVolume size={16} stroke={2} />
                  )}
                </button>
                <input
                  className={classes.headerVolumeRange}
                  type="range"
                  min={0}
                  max={100}
                  value={volumeValue}
                  onChange={(event) =>
                    setVolume(Number(event.target.value) / 100).catch(() => null)
                  }
                  disabled={!ready}
                  aria-label="Volume"
                />
              </div>
              {onHide ? (
                <button
                  type="button"
                  className={classes.headerIconBtn}
                  onClick={onHide}
                  aria-label="Hide player"
                >
                  <IconMinimize size={16} stroke={2} />
                </button>
              ) : null}
            </div>
          </div>
          {showProgressLine ? (
            <div className={classes.headerProgress} aria-hidden>
              <div
                className={classes.headerProgressFill}
                style={{ width: `${ratio * 100}%` }}
              />
            </div>
          ) : null}
          {nowPlayingOpen ? (
            <div className={classes.headerExpand} role="dialog" aria-label="Now playing">
              <NowPlayingBody compact />
            </div>
          ) : null}
        </div>
      </>
    );
  }

  if (mode === 'docked') {
    return (
      <div className={`${classes.dock} ${className}`.trim()}>
        <div className={classes.dockInner}>
          <div className={classes.dockTrack}>
            <Cover
              src={track?.image_url}
              className={
                track?.image_url ? classes.dockArt : classes.dockArtFallback
              }
              size={22}
            />
            <div className={classes.dockMeta}>
              <p className={classes.dockTitle}>
                {track?.name || idle.title}
              </p>
              <p className={classes.dockArtist}>
                {track ? artistNames(track) : idle.subtitle}
              </p>
            </div>
            <button
              type="button"
              className={`${classes.dockIconBtn} ${
                track?.is_saved ? classes.dockIconActive : ''
              }`}
              onClick={() => toggleSave().catch(() => null)}
              disabled={!track?.id || busy}
              aria-label={
                track?.is_saved ? 'Remove from Liked Songs' : 'Save to Liked Songs'
              }
            >
              {track?.is_saved ? (
                <IconHeartFilled size={18} />
              ) : (
                <IconHeart size={18} stroke={2} />
              )}
            </button>
          </div>

          <div className={classes.dockControls}>
            <button
              type="button"
              className={`${classes.dockIconBtn} ${
                playback?.shuffle_state ? classes.dockIconActive : ''
              }`}
              onClick={() => setShuffle(!playback?.shuffle_state).catch(() => null)}
              disabled={busy || !ready}
              aria-label="Shuffle"
            >
              <IconArrowsShuffle size={18} stroke={2} />
            </button>
            <button
              type="button"
              className={classes.dockIconBtn}
              onClick={() => previous().catch(() => null)}
              disabled={busy || !ready}
              aria-label="Previous"
            >
              <IconPlayerSkipBack size={20} stroke={2} />
            </button>
            <button
              type="button"
              className={classes.dockPlayBtn}
              onClick={() => togglePlay().catch(() => null)}
              disabled={busy || !ready || !track}
              aria-label={playback?.is_playing ? 'Pause' : 'Play'}
            >
              {playback?.is_playing ? (
                <IconPlayerPause size={22} stroke={2} />
              ) : (
                <IconPlayerPlay size={22} stroke={2} />
              )}
            </button>
            <button
              type="button"
              className={classes.dockIconBtn}
              onClick={() => next().catch(() => null)}
              disabled={busy || !ready}
              aria-label="Next"
            >
              <IconPlayerSkipForward size={20} stroke={2} />
            </button>
            <button
              type="button"
              className={`${classes.dockIconBtn} ${
                playback?.repeat_state && playback.repeat_state !== 'off'
                  ? classes.dockIconActive
                  : ''
              }`}
              onClick={() => {
                const nextState =
                  playback?.repeat_state === 'off'
                    ? 'context'
                    : playback?.repeat_state === 'context'
                      ? 'track'
                      : 'off';
                setRepeat(nextState).catch(() => null);
              }}
              disabled={busy || !ready}
              aria-label="Repeat"
            >
              <IconRepeat size={18} stroke={2} />
            </button>
          </div>

          <div className={classes.dockRight}>
            <div className={classes.dockScrubber}>
              <span className={classes.dockTime}>{formatClock(progress)}</span>
              <input
                className={classes.dockRange}
                type="range"
                min={0}
                max={duration || 1}
                value={Math.min(progress, duration || 1)}
                onChange={(event) =>
                  seek(Number(event.target.value)).catch(() => null)
                }
                disabled={!track || busy || !ready}
                aria-label="Seek"
              />
              <span className={classes.dockTime}>
                {formatRemaining(progress, duration)}
              </span>
            </div>
            <button
              type="button"
              className={classes.dockIconBtn}
              onClick={() => {
                const target =
                  devices.find((device) => device.id && device.id !== deviceId) ||
                  devices.find((device) => device.id === deviceId);
                if (target?.id) {
                  transferTo(target.id, true).catch(() => null);
                }
              }}
              disabled={busy || !ready || !hasDevices}
              aria-label="Connect device"
              hidden={!hasDevices}
            >
              <IconDevices size={18} stroke={2} />
            </button>
            {onHide ? (
              <button
                type="button"
                className={classes.dockIconBtn}
                onClick={onHide}
                aria-label="Hide player"
              >
                <IconMinimize size={18} stroke={2} />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (!track && mode === 'collapsed') {
    return (
      <div className={`${classes.spotifyGreen} ${className}`.trim()}>
        <button
          type="button"
          className={classes.miniBar}
          onClick={() => {
            if (onExpand) onExpand();
            else setNowPlayingOpen(true);
          }}
        >
          <Cover src={null} className={classes.miniArtFallback} size={20} />
          <div className={classes.miniMeta}>
            <p className={classes.miniTitle}>{idle.title}</p>
            <p className={classes.miniArtist}>{idle.subtitle}</p>
          </div>
          <div className={classes.miniActions}>
            <IconChevronUp size={16} stroke={2.25} />
          </div>
        </button>
      </div>
    );
  }

  if (mode === 'expanded') {
    return (
      <div className={`${classes.spotifyGreen} ${classes.expandedCard} ${className}`.trim()}>
        <div className={classes.expandedTop}>
          <Cover
            src={track?.image_url}
            className={track?.image_url ? classes.expandedArt : classes.expandedArtFallback}
          />
          <div className={classes.miniMeta}>
            <p className={classes.miniTitle}>{track?.name || idle.title}</p>
            <p className={classes.miniArtist}>
              {track ? artistNames(track) : idle.subtitle}
            </p>
          </div>
          <div className={classes.miniActions}>
            <button
              type="button"
              className={`${classes.iconBtn} ${track?.is_saved ? classes.iconBtnActive : ''}`}
              onClick={() => toggleSave().catch(() => null)}
              disabled={!track?.id || busy}
              aria-label={track?.is_saved ? 'Remove from Liked Songs' : 'Save to Liked Songs'}
            >
              {track?.is_saved ? (
                <IconHeartFilled size={18} />
              ) : (
                <IconHeart size={18} stroke={2} />
              )}
            </button>
            <button
              type="button"
              className={classes.iconBtn}
              onClick={() => {
                if (onCollapse) onCollapse();
                else setNowPlayingOpen(false);
              }}
              aria-label="Collapse player"
            >
              <IconChevronDown size={18} stroke={2.25} />
            </button>
          </div>
        </div>

        <div className={classes.scrubberWrap}>
          <p className={classes.time}>{formatClock(progress)}</p>
          <input
            className={classes.scrubber}
            type="range"
            min={0}
            max={duration || 1}
            value={Math.min(progress, duration || 1)}
            onChange={(event) => seek(Number(event.target.value)).catch(() => null)}
            disabled={!track || busy || !ready}
            aria-label="Seek"
          />
          <p className={classes.time}>{formatRemaining(progress, duration)}</p>
        </div>

        <div className={classes.controlsRow}>
          <button
            type="button"
            className={`${classes.iconBtn} ${playback.shuffle_state ? classes.iconBtnActive : ''}`}
            onClick={() => setShuffle(!playback.shuffle_state).catch(() => null)}
            disabled={busy || !ready}
            aria-label="Shuffle"
          >
            <IconArrowsShuffle size={18} stroke={2} />
          </button>
          <button
            type="button"
            className={classes.iconBtn}
            onClick={() => previous().catch(() => null)}
            disabled={busy || !ready}
            aria-label="Previous"
          >
            <IconPlayerSkipBack size={20} stroke={2} />
          </button>
          <button
            type="button"
            className={classes.playBtn}
            onClick={() => togglePlay().catch(() => null)}
            disabled={busy || !ready || !track}
            aria-label={playback.is_playing ? 'Pause' : 'Play'}
          >
            {playback.is_playing ? (
              <IconPlayerPause size={22} stroke={2} />
            ) : (
              <IconPlayerPlay size={22} stroke={2} />
            )}
          </button>
          <button
            type="button"
            className={classes.iconBtn}
            onClick={() => next().catch(() => null)}
            disabled={busy || !ready}
            aria-label="Next"
          >
            <IconPlayerSkipForward size={20} stroke={2} />
          </button>
          <button
            type="button"
            className={classes.iconBtn}
            onClick={() => {
              const target =
                devices.find((device) => device.id && device.id !== deviceId) ||
                devices.find((device) => device.id === deviceId);
              if (target?.id) {
                transferTo(target.id, true).catch(() => null);
              }
            }}
            disabled={busy || !ready || !hasDevices}
            aria-label="Connect device"
            hidden={!hasDevices}
          >
            <IconDevices size={18} stroke={2} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${classes.spotifyGreen} ${className}`.trim()}>
      <button
        type="button"
        className={classes.miniBar}
        onClick={() => {
          if (onExpand) onExpand();
          else setNowPlayingOpen(true);
        }}
      >
        <Cover
          src={track?.image_url}
          className={track?.image_url ? classes.miniArt : classes.miniArtFallback}
          size={20}
        />
        <div className={classes.miniMeta}>
          <p className={classes.miniTitle}>{track?.name || 'Nothing playing'}</p>
          <p className={classes.miniArtist}>
            {track ? artistNames(track) : 'Tap to open player'}
          </p>
        </div>
        <div className={classes.miniActions}>
          <span
            role="button"
            tabIndex={0}
            className={classes.iconBtn}
            onClick={(event) => {
              event.stopPropagation();
              togglePlay().catch(() => null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                togglePlay().catch(() => null);
              }
            }}
            aria-label={playback.is_playing ? 'Pause' : 'Play'}
          >
            {playback.is_playing ? (
              <IconPlayerPause size={18} stroke={2} />
            ) : (
              <IconPlayerPlay size={18} stroke={2} />
            )}
          </span>
          <IconChevronUp size={16} stroke={2.25} />
        </div>
        {showProgressLine ? (
          <div className={classes.miniBarProgress} aria-hidden>
            <div
              className={classes.miniBarProgressFill}
              style={{ width: `${ratio * 100}%` }}
            />
          </div>
        ) : null}
      </button>
    </div>
  );
}

export function SpotifyNowPlayingSheet() {
  const { nowPlayingOpen, headerPlayerMounted } = useAppleMusicPlayer();

  if (!nowPlayingOpen || headerPlayerMounted) return null;

  return (
    <div className={`${classes.spotifyGreen} ${classes.sheet}`}>
      <NowPlayingBody />
    </div>
  );
}
